import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { toast } from 'sonner';
import { 
  Shield, 
  Key,
  User,
  Lock,
  ChevronRight,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';

export default function SettingsPage() {
  const { user, api, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [disablingMfa, setDisablingMfa] = useState(false);
  const [pinStatus, setPinStatus] = useState(null);

  useEffect(() => {
    const fetchPinStatus = async () => {
      try {
        const response = await api.get('/panic/pin/status');
        setPinStatus(response.data);
      } catch (error) {
        console.error('Failed to fetch PIN status:', error);
      }
    };
    fetchPinStatus();
  }, [api]);

  const handleSetupMfa = () => {
    navigate('/mfa-setup');
  };

  const handleDisableMfa = async () => {
    const code = prompt('Enter your 2FA code to disable MFA:');
    if (!code) return;

    setDisablingMfa(true);
    try {
      await api.post('/auth/mfa/disable', null, { params: { totp_code: code } });
      toast.success('MFA disabled successfully');
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable MFA');
    } finally {
      setDisablingMfa(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-8 max-w-3xl" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl lg:text-4xl font-black text-[#E6F1FF]">Settings</h1>
          <p className="text-[#8892B0] mt-1">Manage your account and security preferences</p>
        </div>

        {/* Profile Section */}
        <div className="glass-card rounded-2xl p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#2BBBAD]/20 flex items-center justify-center">
              <User className="w-6 h-6 text-[#2BBBAD]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#E6F1FF]">Profile</h2>
              <p className="text-sm text-[#8892B0]">Your account information</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-white/5">
              <span className="text-[#8892B0]">Full Name</span>
              <span className="text-[#E6F1FF] font-medium">{user?.full_name}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-white/5">
              <span className="text-[#8892B0]">Email</span>
              <span className="text-[#E6F1FF] font-medium">{user?.email}</span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-[#8892B0]">Account Type</span>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${user?.is_admin ? 'bg-[#64FFDA]/20 text-[#64FFDA]' : 'bg-[#2BBBAD]/20 text-[#2BBBAD]'}`}>
                {user?.is_admin ? 'Administrator' : 'User'}
              </span>
            </div>
          </div>
        </div>

        {/* Security Section */}
        <div className="glass-card rounded-2xl p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#64FFDA]/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#64FFDA]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#E6F1FF]">Security</h2>
              <p className="text-sm text-[#8892B0]">Manage your security settings</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* 2FA Setting */}
            <div className="flex items-center justify-between py-4 px-4 rounded-xl bg-[#0B1C2D]/50">
              <div className="flex items-center gap-4">
                <Key className="w-5 h-5 text-[#2BBBAD]" />
                <div>
                  <p className="text-[#E6F1FF] font-medium">Two-Factor Authentication</p>
                  <p className="text-xs text-[#8892B0]">Add an extra layer of security</p>
                </div>
              </div>
              {user?.mfa_enabled ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDisableMfa}
                  disabled={disablingMfa}
                  className="border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10"
                  data-testid="disable-mfa-button"
                >
                  Disable
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={handleSetupMfa}
                  className="bg-[#2BBBAD] hover:bg-[#3FD2C7] text-[#0B1C2D]"
                  data-testid="setup-mfa-button"
                >
                  Enable
                </Button>
              )}
            </div>

            {/* MFA Status */}
            <div className="flex items-center justify-between py-3 border-b border-white/5">
              <span className="text-[#8892B0]">MFA Status</span>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${user?.mfa_enabled ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#FFCC00]/20 text-[#FFCC00]'}`}>
                {user?.mfa_enabled ? 'Enabled' : 'Not Enabled'}
              </span>
            </div>

            {/* Panic PIN Setting */}
            <div className="flex items-center justify-between py-4 px-4 rounded-xl bg-[#0B1C2D]/50">
              <div className="flex items-center gap-4">
                <Lock className="w-5 h-5 text-[#FFCC00]" />
                <div>
                  <p className="text-[#E6F1FF] font-medium">Panic PIN</p>
                  <p className="text-xs text-[#8892B0]">Required for panic activation</p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${pinStatus?.has_pin ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#FFCC00]/20 text-[#FFCC00]'}`}>
                {pinStatus?.has_pin ? 'Set Up' : 'Not Set'}
              </span>
            </div>

            {/* PIN info if set */}
            {pinStatus?.has_pin && pinStatus?.pin_created_at && (
              <div className="flex items-center justify-between py-3 border-b border-white/5 pl-4">
                <span className="text-[#8892B0] text-sm">PIN Created</span>
                <span className="text-xs text-[#8892B0]">
                  {new Date(pinStatus.pin_created_at).toLocaleDateString()}
                </span>
              </div>
            )}

            {/* Panic Mode Status */}
            <div className="flex items-center justify-between py-3">
              <span className="text-[#8892B0]">System Status</span>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${user?.panic_mode ? 'bg-[#FF3B30]/20 text-[#FF3B30]' : 'bg-[#10B981]/20 text-[#10B981]'}`}>
                {user?.panic_mode ? 'Panic Mode' : 'Safe'}
              </span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="glass-card rounded-2xl p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#3B82F6]/20 flex items-center justify-center">
              <Lock className="w-6 h-6 text-[#3B82F6]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#E6F1FF]">Quick Actions</h2>
              <p className="text-sm text-[#8892B0]">Common security actions</p>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => navigate('/partners')}
              className="w-full flex items-center justify-between p-4 rounded-xl bg-[#0B1C2D]/50 hover:bg-[#1A2F4B]/50 transition-colors"
              data-testid="manage-partners-link"
            >
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-[#2BBBAD]" />
                <span className="text-[#E6F1FF]">Manage Protected Partners</span>
              </div>
              <ChevronRight className="w-5 h-5 text-[#8892B0]" />
            </button>

            <button
              onClick={() => navigate('/audit')}
              className="w-full flex items-center justify-between p-4 rounded-xl bg-[#0B1C2D]/50 hover:bg-[#1A2F4B]/50 transition-colors"
              data-testid="view-audit-link"
            >
              <div className="flex items-center gap-3">
                <Key className="w-5 h-5 text-[#64FFDA]" />
                <span className="text-[#E6F1FF]">View Audit Log</span>
              </div>
              <ChevronRight className="w-5 h-5 text-[#8892B0]" />
            </button>

            {user?.panic_mode && (
              <button
                onClick={() => navigate('/recovery')}
                className="w-full flex items-center justify-between p-4 rounded-xl bg-[#FF3B30]/10 border border-[#FF3B30]/30 hover:bg-[#FF3B30]/20 transition-colors"
                data-testid="start-recovery-link"
              >
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-[#FF3B30]" />
                  <span className="text-[#FF3B30]">Start Recovery Process</span>
                </div>
                <ChevronRight className="w-5 h-5 text-[#FF3B30]" />
              </button>
            )}
          </div>
        </div>

        {/* Danger Zone */}
        <div className="glass-card rounded-2xl p-6 border border-[#FF3B30]/20">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#FF3B30]/20 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-[#FF3B30]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#FF3B30]">Danger Zone</h2>
              <p className="text-sm text-[#8892B0]">Irreversible actions</p>
            </div>
          </div>

          <Button
            variant="outline"
            onClick={() => {
              if (window.confirm('Are you sure you want to logout?')) {
                logout();
                navigate('/login');
              }
            }}
            className="w-full border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10"
            data-testid="logout-settings-button"
          >
            Sign Out
          </Button>
        </div>
      </div>
    </Layout>
  );
}
